import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { usePolling } from "@/lib/usePolling";
import { fmtShort, fmtDateTime } from "@/lib/datetime";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, UserPlus, Download, Users, Activity, ShieldOff, ShieldCheck, Trash2, Search, KeyRound, Copy, Image as ImageIcon, CalendarRange, Map as MapIcon, Trophy, Pencil, DollarSign, Workflow, MessageCircle } from "lucide-react";
import BootMap from "@/components/BootMap";
import PmcPayLookup from "@/components/PmcPayLookup";
import EvidenceGallery, { mediaItems } from "@/components/EvidenceGallery";
import VehicleEditModal from "@/components/VehicleEditModal";
import MediaViewer from "@/components/MediaViewer";
import Chat from "@/components/Chat";
import WhatsAppButton, { bootMessage, setRemovalWhatsApp } from "@/components/WhatsAppButton";
import { API } from "@/lib/api";

const statusClass = { tagged: "status-tag", booted: "status-boot", paid: "status-paid", removed: "status-removed", not_booted: "status-notbooted" };

export default function AdminDashboard() {
  const [stats, setStats] = useState({ tagged: 0, booted: 0, removed: 0, active_boots: 0 });
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));
  const [newUser, setNewUser] = useState({ email: "", full_name: "", role: "enforcer", password: "", phone: "" });
  const [reportBlobUrl, setReportBlobUrl] = useState(null);
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day);
    return d.toISOString().slice(0, 10);
  });
  const [weekBlobUrl, setWeekBlobUrl] = useState(null);
  const [search, setSearch] = useState("");
  const [invites, setInvites] = useState([]);
  const [mediaFor, setMediaFor] = useState(null);
  const [editVehicle, setEditVehicle] = useState(null);
  const [mapOfficer, setMapOfficer] = useState(null);
  const [leaderPeriod, setLeaderPeriod] = useState("week");
  const [leaders, setLeaders] = useState([]);
  const [payWeek, setPayWeek] = useState(() => {
    const d = new Date(); const back = (d.getDay() - 2 + 7) % 7; d.setDate(d.getDate() - back);
    return d.toISOString().slice(0, 10);
  });
  const [payroll, setPayroll] = useState(null);
  const [payrollBlobUrl, setPayrollBlobUrl] = useState(null);
  const [flowDate, setFlowDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [flow, setFlow] = useState([]);
  const [chatCar, setChatCar] = useState(null);
  const [waNumber, setWaNumber] = useState("");
  const [waSaving, setWaSaving] = useState(false);
  const todayStr = new Date().toISOString().slice(0, 10);
  const todayVehicles = vehicles.filter((v) => v.shift_date === todayStr);
  const officers = [...new Set(vehicles.map((v) => v.enforcer_name))].sort();
  const mapVehicles = mapOfficer ? todayVehicles.filter((v) => v.enforcer_name === mapOfficer) : todayVehicles;

  const OfficerChips = ({ source, testPrefix }) => (
    <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-officer-filter`}>
      <button type="button" onClick={() => setMapOfficer(null)}
        className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${!mapOfficer ? "bg-cyan-500 text-[#0B0F17] border-cyan-500" : "bg-[#0B0F17] text-[#94A3B8] border-[#28364F] hover:border-cyan-500/50"}`}
        data-testid={`${testPrefix}-officer-filter-all`}>All officers ({source.length})</button>
      {officers.map((o) => {
        const n = source.filter((v) => v.enforcer_name === o).length;
        return (
          <button key={o} type="button" onClick={() => setMapOfficer(mapOfficer === o ? null : o)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${mapOfficer === o ? "bg-cyan-500 text-[#0B0F17] border-cyan-500" : "bg-[#0B0F17] text-[#94A3B8] border-[#28364F] hover:border-cyan-500/50"}`}
            data-testid={`${testPrefix}-officer-filter-${o.replace(/\s+/g, "-").toLowerCase()}`}>{o} ({n})</button>
        );
      })}
    </div>
  );

  const loadInvites = async () => {
    const r = await api.get("/invites");
    setInvites(r.data);
  };

  const createInvite = async () => {
    try {
      const r = await api.post("/invites");
      const link = `${window.location.origin}/signup?invite=${r.data.code}`;
      try { await navigator.clipboard.writeText(link); toast.success(`Invite ${r.data.code} created — link copied`); }
      catch { toast.success(`Invite ${r.data.code} created`); }
      loadInvites();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to create invite"); }
  };

  const copyInvite = async (code) => {
    const link = `${window.location.origin}/signup?invite=${code}`;
    try { await navigator.clipboard.writeText(link); toast.success("Invite link copied"); }
    catch { window.prompt("Copy this invite link:", link); }
  };

  const revokeInvite = async (code) => {
    await api.delete(`/invites/${code}`);
    loadInvites();
  };

  const filteredVehicles = vehicles.filter((v) => {
    if (mapOfficer && v.enforcer_name !== mapOfficer) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      v.plate_number.toLowerCase().includes(q) ||
      v.location.toLowerCase().includes(q) ||
      v.enforcer_name.toLowerCase().includes(q) ||
      v.status.toLowerCase().includes(q) ||
      (v.removal_agent_name || "").toLowerCase().includes(q)
    );
  });

  const load = async () => {
    const [s, v, u] = await Promise.all([
      api.get("/stats/today"),
      api.get("/vehicles?scope=all"),
      api.get("/users"),
    ]);
    setStats(s.data);
    setVehicles(v.data);
    setUsers(u.data);
  };

  useEffect(() => { loadInvites(); }, []);
  useEffect(() => { api.get("/settings/whatsapp").then((r) => setWaNumber(r.data?.removal_whatsapp || "")).catch(() => {}); }, []);

  const saveWhatsApp = async () => {
    setWaSaving(true);
    try {
      const r = await api.put("/settings/whatsapp", { number: waNumber });
      const val = r.data?.removal_whatsapp || "";
      setWaNumber(val);
      setRemovalWhatsApp(val);
      toast.success(val ? "Removal WhatsApp number saved" : "Removal WhatsApp number cleared — buttons will use the contact picker");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setWaSaving(false); }
  };

  const loadLeaders = async (p = leaderPeriod) => {
    try { setLeaders((await api.get(`/reports/runner-leaderboard?period=${p}`)).data.runners || []); }
    catch { setLeaders([]); }
  };

  const loadPayroll = async (wk = payWeek) => {
    try { setPayroll((await api.get(`/reports/payroll-data?week_start=${wk}`)).data); } catch { setPayroll(null); }
  };
  useEffect(() => { loadPayroll(payWeek); }, [payWeek]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadFlow = async (dt = flowDate) => { try { setFlow((await api.get(`/reports/flow?date=${dt}`)).data.events || []); } catch { setFlow([]); } };
  usePolling(() => { load(); loadLeaders(leaderPeriod); loadFlow(flowDate); }, 10000, [leaderPeriod, flowDate]);
  const deleteVehicle = async (v) => {
    if (!window.confirm(`Delete ${v.plate_number}? This permanently removes the record and its chat.`)) return;
    try { await api.delete(`/vehicles/${v.id}`); toast.success(`${v.plate_number} deleted`); load(); loadFlow(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const previewPayroll = async () => {
    try { setPayrollBlobUrl(await fetchPdf(`${API}/reports/payroll?week_start=${payWeek}`)); }
    catch (_e) { toast.error("Failed to load payroll PDF"); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", newUser);
      toast.success(`Added ${newUser.email}`);
      setNewUser({ email: "", full_name: "", role: "enforcer", password: "", phone: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(`New temporary password for ${u.full_name} (${u.email}) — at least 6 characters:`);
    if (pw == null) return;
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.patch(`/users/${u.id}`, { password: pw });
      toast.success(`Password reset for ${u.email} — share it with them securely`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Reset failed"); }
  };

  const toggleActive = async (u) => {
    await api.patch(`/users/${u.id}`, { active: !u.active });
    load();
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const previewReport = async () => {
    try {
      const token = localStorage.getItem("pl_token");
      const res = await fetch(`${API}/reports/daily?shift_date=${reportDate}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setReportBlobUrl(url);
    } catch (_e) { toast.error("Failed to load report"); }
  };

  const downloadReport = async () => {
    if (!reportBlobUrl) await previewReport();
    const a = document.createElement("a");
    a.href = reportBlobUrl || "#";
    a.download = `parklock-shift-${reportDate}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const fetchPdf = async (url) => {
    const token = localStorage.getItem("pl_token");
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error("failed");
    return URL.createObjectURL(await res.blob());
  };

  const previewWeekly = async () => {
    try { setWeekBlobUrl(await fetchPdf(`${API}/reports/weekly?week_start=${weekStart}`)); }
    catch (_e) { toast.error("Failed to load weekly report"); }
  };

  const downloadWeekly = async () => {
    let url = weekBlobUrl;
    if (!url) { try { url = await fetchPdf(`${API}/reports/weekly?week_start=${weekStart}`); setWeekBlobUrl(url); } catch { toast.error("Failed"); return; } }
    const a = document.createElement("a");
    a.href = url; a.download = `parklock-week-${weekStart}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const kpi = [
    { label: "Tagged Today", value: stats.tagged, color: "text-blue-300", accent: "border-blue-500/30 bg-blue-500/5" },
    { label: "Booted Today", value: stats.booted, color: "text-red-300", accent: "border-red-500/30 bg-red-500/5" },
    { label: "Active Boots", value: stats.active_boots, color: "text-amber-300", accent: "border-amber-500/30 bg-amber-500/5" },
    { label: "Removed Today", value: stats.removed, color: "text-emerald-300", accent: "border-emerald-500/30 bg-emerald-500/5" },
    { label: "Fees Collected", value: `$${Number(stats.fees_collected || 0).toFixed(2)}`, color: "text-amber-200", accent: "border-amber-400/40 bg-amber-400/10 col-span-2 md:col-span-1",
      sub: stats.fees_by_method ? `Cash $${Number(stats.fees_by_method.cash || 0).toFixed(2)} · Card $${Number(stats.fees_by_method.card || 0).toFixed(2)}${stats.fees_by_method.other ? ` · Other $${Number(stats.fees_by_method.other).toFixed(2)}` : ""}` : null },
  ];

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Command Console</h1>
          <p className="text-sm text-[#94A3B8]">Live oversight — {new Date().toDateString()}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {kpi.map((k) => (
          <Card key={k.label} className={`p-4 border ${k.accent}`}>
            <div className={`text-[10px] uppercase tracking-widest ${k.color}`}>{k.label}</div>
            <div className="text-3xl font-black font-display mt-1" data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}>{k.value}</div>
            {k.sub && <div className="text-[11px] text-amber-200/80 mt-1 font-mono" data-testid="kpi-fees-breakdown">{k.sub}</div>}
          </Card>
        ))}
      </div>

      <Tabs defaultValue="activity">
        <TabsList className="bg-[#131B2A] border border-[#28364F] w-full grid grid-cols-7 h-12">
          <TabsTrigger value="activity" data-testid="tab-activity" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-[#0B0F17]">
            <Activity className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">Activity</span>
          </TabsTrigger>
          <TabsTrigger value="map" data-testid="tab-map" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-[#0B0F17]">
            <MapIcon className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">Map</span>
          </TabsTrigger>
          <TabsTrigger value="runners" data-testid="tab-runners" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-[#0B0F17]">
            <Trophy className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">Runners</span>
          </TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-[#0B0F17]">
            <DollarSign className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">Payroll</span>
          </TabsTrigger>
          <TabsTrigger value="flow" data-testid="tab-flow" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-[#0B0F17]">
            <Workflow className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">Flow</span>
          </TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-[#0B0F17]">
            <Users className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">Team</span>
          </TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-[#0B0F17]">
            <FileText className="w-4 h-4 mr-1.5" /> <span className="hidden sm:inline">Reports</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="map" className="mt-4">
          <Card className="bg-[#131B2A] border-[#28364F] p-4 space-y-3">
            <div>
              <div className="font-display font-bold flex items-center gap-2"><MapIcon className="w-4 h-4 text-cyan-400" /> City-Wide Live Map — today</div>
              <p className="text-xs text-[#94A3B8]">Every officer's tagged, booted and removed vehicles for {new Date().toDateString()}. Refreshes every 10s.</p>
            </div>
            {officers.length > 0 && <OfficerChips source={todayVehicles} testPrefix="map" />}
            <BootMap vehicles={mapVehicles} showLegend testId="admin-map"
              emptyLabel={mapOfficer ? `${mapOfficer} has no vehicles today.` : "No vehicles logged today yet."}
              noGpsLabel="Today's vehicles have no GPS coordinates — enforcers' Tag form now auto-captures location." />
          </Card>
        </TabsContent>

        <TabsContent value="runners" className="mt-4">
          <Card className="bg-[#131B2A] border-[#28364F] p-5 space-y-4" data-testid="runner-leaderboard">
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div>
                <div className="font-display font-bold flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-400" /> Runner Leaderboard</div>
                <p className="text-xs text-[#94A3B8]">Top plate runners by tags that got booted and paid. Refreshes every 10s.</p>
              </div>
              <div className="flex gap-1.5">
                {[["today", "Today"], ["week", "This Week"], ["all", "All Time"]].map(([val, lbl]) => (
                  <Button key={val} size="sm" onClick={() => setLeaderPeriod(val)}
                    data-testid={`leader-period-${val}`}
                    className={leaderPeriod === val ? "bg-amber-500 hover:bg-amber-400 text-[#0B0F17] font-bold" : "bg-[#0B0F17] border border-[#28364F] text-[#94A3B8] hover:text-white"}>
                    {lbl}
                  </Button>
                ))}
              </div>
            </div>
            {leaders.length === 0 ? (
              <div className="text-sm text-[#64748B] py-8 text-center" data-testid="leader-empty">No plate runner tags in this period yet.</div>
            ) : (
              <div className="space-y-2">
                {leaders.map((r, i) => {
                  const bootPct = r.tags ? Math.round((r.booted / r.tags) * 100) : 0;
                  const paidPct = r.tags ? Math.round((r.paid / r.tags) * 100) : 0;
                  const medal = ["text-amber-400", "text-slate-300", "text-orange-400"][i] || "text-[#64748B]";
                  return (
                    <div key={r.runner} className="flex items-center gap-3 rounded-lg border border-[#28364F] bg-[#0B0F17] p-3" data-testid={`leader-row-${i}`}>
                      <div className={`font-black font-display text-lg w-8 text-center ${medal}`}>#{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-white truncate" data-testid="leader-name">{r.runner}</div>
                        <div className="text-[11px] text-[#94A3B8] font-mono">{r.tags} tagged · {bootPct}% booted · {paidPct}% paid</div>
                      </div>
                      <div className="flex gap-4 text-center shrink-0">
                        <div><div className="text-lg font-black text-blue-300">{r.tags}</div><div className="text-[9px] uppercase tracking-widest text-[#64748B]">Tags</div></div>
                        <div><div className="text-lg font-black text-red-300">{r.booted}</div><div className="text-[9px] uppercase tracking-widest text-[#64748B]">Booted</div></div>
                        <div><div className="text-lg font-black text-emerald-300">{r.paid}</div><div className="text-[9px] uppercase tracking-widest text-[#64748B]">Paid</div></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="payroll" className="mt-4 space-y-4">
          <Card className="bg-[#131B2A] border-emerald-500/30 p-5 space-y-4" data-testid="payroll-panel">
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div>
                <div className="font-display font-bold flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-400" /> Weekly Payroll</div>
                <p className="text-xs text-[#94A3B8] max-w-lg">Pay week Tue → Mon, payday Tue. Booter pay = $20.00 per completed boot (paid &amp; removed), $12.50 per paid-to-park wrong-entry ($28 take-off). Hours come from the time clock.</p>
              </div>
              <div className="flex items-end gap-2 flex-wrap">
                <div>
                  <Label>Week starting (Tuesday)</Label>
                  <Input type="date" value={payWeek} onChange={(e) => { setPayWeek(e.target.value); setPayrollBlobUrl(null); }}
                    className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="payroll-week-input" />
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button onClick={previewPayroll} className="bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-bold" data-testid="payroll-pdf-btn">
                      <FileText className="w-4 h-4 mr-1.5" /> Payroll PDF
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#131B2A] border-[#28364F] text-white max-w-4xl h-[85vh] flex flex-col">
                    <DialogHeader>
                      <DialogTitle className="flex items-center justify-between">
                        <span>Weekly Payroll — {payroll ? `${payroll.week_start} to ${payroll.week_end}` : payWeek}</span>
                        {payrollBlobUrl && (
                          <a href={payrollBlobUrl} download={`parklock-payroll-${payWeek}.pdf`}>
                            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-bold" data-testid="payroll-download-btn"><Download className="w-4 h-4 mr-1.5" /> Download</Button>
                          </a>
                        )}
                      </DialogTitle>
                    </DialogHeader>
                    {payrollBlobUrl ? (
                      <iframe src={payrollBlobUrl} title="Payroll" className="flex-1 w-full bg-white rounded border border-[#28364F]" data-testid="payroll-iframe" />
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-[#94A3B8]">Loading payroll…</div>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            {payroll && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-emerald-300 mb-2">Booter Pay · total ${Number(payroll.total_booter_pay).toFixed(2)}</div>
                  {payroll.booters.length === 0 ? (
                    <div className="text-sm text-[#64748B] py-4" data-testid="payroll-booters-empty">No completed boots in this pay week.</div>
                  ) : (
                    <div className="space-y-1.5" data-testid="payroll-booters">
                      {payroll.booters.map((b) => (
                        <div key={b.name} className="flex items-center justify-between rounded border border-[#28364F] bg-[#0B0F17] p-2.5 text-sm">
                          <div><div className="font-semibold text-white">{b.name}</div><div className="text-[11px] text-[#94A3B8] font-mono">{b.standard} std · {b.incorrect} wrong-entry</div></div>
                          <div className="font-black text-emerald-300">${Number(b.earnings).toFixed(2)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-300 mb-2">Hours Clocked · total {Number(payroll.total_hours).toFixed(2)}h</div>
                  {payroll.hours.length === 0 ? (
                    <div className="text-sm text-[#64748B] py-4" data-testid="payroll-hours-empty">No clocked hours in this pay week.</div>
                  ) : (
                    <div className="space-y-1.5" data-testid="payroll-hours">
                      {payroll.hours.map((h) => (
                        <div key={h.name} className="flex items-center justify-between rounded border border-[#28364F] bg-[#0B0F17] p-2.5 text-sm">
                          <div><div className="font-semibold text-white">{h.name}</div><div className="text-[11px] text-[#94A3B8] font-mono uppercase">{h.role} · {h.shifts} shift{h.shifts === 1 ? "" : "s"}{h.open ? ` · ${h.open} open` : ""}</div></div>
                          <div className="font-black text-amber-300">{Number(h.hours).toFixed(2)}h</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="flow" className="mt-4 space-y-3">
          <Card className="bg-[#131B2A] border-[#28364F] p-4 space-y-3" data-testid="flow-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-display font-bold flex items-center gap-2"><Workflow className="w-4 h-4 text-cyan-400" /> Activity Flow</div>
              <Input type="date" value={flowDate} onChange={(e) => setFlowDate(e.target.value)} className="bg-[#0B0F17] border-[#28364F] text-white w-auto" data-testid="flow-date-input" />
            </div>
            {flow.length === 0 ? (
              <div className="text-sm text-[#64748B] py-8 text-center" data-testid="flow-empty">No activity on this day.</div>
            ) : (
              <div className="space-y-2" data-testid="flow-list">
                {flow.map((e, i) => {
                  const c = { tagged: "border-blue-500/50 text-blue-300", booted: "border-red-500/50 text-red-300", not_booted: "border-slate-500/50 text-slate-300", paid: "border-amber-500/50 text-amber-300", removed: "border-emerald-500/50 text-emerald-300" }[e.type] || "border-[#28364F] text-white";
                  return (
                    <div key={`${e.type}-${e.plate}-${e.at}`} className={`flex items-center gap-3 rounded-lg border bg-[#0B0F17] p-2.5 ${c}`} data-testid={`flow-event-${i}`}>
                      {e.photo ? <div className="w-12 h-12 shrink-0"><MediaViewer path={e.photo} label={e.type} className="h-12" testId={`flow-thumb-${i}`} /></div> : <div className="w-12 h-12 shrink-0 rounded bg-[#131B2A] border border-[#28364F]" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white flex items-center gap-1.5 flex-wrap">
                          <span className="plate-badge">{e.plate}</span>
                          <span className={`uppercase text-[11px] font-bold ${c}`}>{e.type.replace("_", " ")}</span>
                          {e.pmc_paid_flag && <span className="text-[9px] uppercase px-1 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40">PMC PAID</span>}
                        </div>
                        <div className="text-[11px] text-[#94A3B8]">by {e.who || "—"} · {fmtDateTime(e.at)}{e.location ? ` · ${e.location}` : ""}{e.type === "paid" && e.fee ? ` · $${Number(e.fee).toFixed(2)} ${e.method || ""}` : ""}{e.reason ? ` · ${e.reason}` : ""}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="mt-4 space-y-3">
          <PmcPayLookup />
          {officers.length > 0 && <OfficerChips source={vehicles} testPrefix="activity" />}
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search plate, location, enforcer, status..."
              className="bg-[#131B2A] border-[#28364F] text-white pl-9"
              data-testid="activity-search-input"
            />
            {(search || mapOfficer) && (
              <div className="text-xs text-[#94A3B8] mt-1.5" data-testid="activity-search-count">
                {filteredVehicles.length} match{filteredVehicles.length === 1 ? "" : "es"}{mapOfficer ? ` for ${mapOfficer}` : ""}
              </div>
            )}
          </div>
          <Card className="bg-[#131B2A] border-[#28364F] overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#28364F] hover:bg-transparent">
                    <TableHead>Plate</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Enforcer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Tagged</TableHead>
                    <TableHead>Booted</TableHead>
                    <TableHead>Removed</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Fee</TableHead>
                    <TableHead>Media</TableHead>
                    <TableHead>Edit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVehicles.slice(0, 200).map((v) => (
                    <TableRow key={v.id} className="border-[#28364F] hover:bg-[#1C263B]">
                      <TableCell><span className="plate-badge">{v.plate_number}</span>{v.pmc_paid_flag && <span className="ml-1 text-[9px] uppercase px-1 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/40" title={`PMC ${v.pmc_flag_info?.status || ""} @ ${v.pmc_flag_info?.lot_name || ""}`} data-testid={`pmc-flag-${v.plate_number}`}>PMC PAID</span>}</TableCell>
                      <TableCell className="text-[#94A3B8]">{v.location}</TableCell>
                      <TableCell>{v.enforcer_name}</TableCell>
                      <TableCell><Badge className={`${statusClass[v.status]} uppercase text-[10px]`}>{v.status}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{fmtShort(v.tagged_at)}</TableCell>
                      <TableCell className="font-mono text-xs text-red-300">{fmtShort(v.booted_at)}</TableCell>
                      <TableCell className="font-mono text-xs text-emerald-300">{fmtShort(v.removed_at)}</TableCell>
                      <TableCell className="text-[#94A3B8]">{v.removal_agent_name || "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-amber-300">{["paid", "removed"].includes(v.status) ? `$${Number(v.fee_amount || 0).toFixed(2)} ${v.payment_method || ""}` : "—"}</TableCell>
                      <TableCell>
                        {mediaItems(v).length ? (
                          <Button size="sm" variant="ghost" onClick={() => setMediaFor(v)} data-testid={`media-btn-${v.plate_number}`}>
                            <ImageIcon className="w-4 h-4 text-cyan-400" />
                          </Button>
                        ) : <span className="text-[#64748B]">—</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setEditVehicle(v)} data-testid={`edit-btn-${v.plate_number}`}><Pencil className="w-4 h-4 text-amber-400" /></Button>
                          {["booted", "paid", "removed", "not_booted"].includes(v.status) && <Button size="sm" variant="ghost" onClick={() => setChatCar(v)} data-testid={`chat-btn-${v.plate_number}`}><MessageCircle className="w-4 h-4 text-cyan-400" /></Button>}
                          {["booted", "paid"].includes(v.status) && <WhatsAppButton message={bootMessage(v)} label="Send to removal team" iconOnly variant="ghost" className="bg-transparent border-0 px-2" testId={`whatsapp-btn-${v.plate_number}`} />}
                          <Button size="sm" variant="ghost" onClick={() => deleteVehicle(v)} data-testid={`delete-btn-${v.plate_number}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredVehicles.length === 0 && (
                    <TableRow><TableCell colSpan={11} className="text-center text-[#94A3B8] py-8">{vehicles.length === 0 ? "No activity yet" : "No matches"}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="team" className="mt-4 space-y-4">
          <Card className="bg-[#131B2A] border-[#25D366]/30 p-4 space-y-3" data-testid="whatsapp-settings-card">
            <div>
              <div className="font-display font-bold flex items-center gap-2 text-[#25D366]"><MessageCircle className="w-4 h-4" /> Removal Team WhatsApp</div>
              <p className="text-xs text-[#94A3B8] max-w-xl">Set the removal dispatcher's WhatsApp number. Every "Send plate to removal team" button then opens that exact chat with the plate &amp; location already typed. Leave blank to let staff pick a contact each time. Use international format, e.g. +1 713 555 0142.</p>
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <Label>WhatsApp number</Label>
                <Input value={waNumber} onChange={(e) => setWaNumber(e.target.value)} placeholder="+1 713 555 0142"
                  className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="whatsapp-number-input" />
              </div>
              <Button onClick={saveWhatsApp} disabled={waSaving} className="bg-[#25D366] hover:brightness-110 text-[#0B0F17] font-bold" data-testid="whatsapp-save-btn">
                {waSaving ? "Saving..." : "Save number"}
              </Button>
            </div>
          </Card>
          <Card className="bg-[#131B2A] border-amber-500/30 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-display font-bold flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-400" /> Admin Invite Codes</div>
                <p className="text-xs text-[#94A3B8]">One-time links so only trusted teammates can sign up as Admin.</p>
              </div>
              <Button onClick={createInvite} className="bg-amber-500 hover:bg-amber-400 text-[#0B0F17] font-bold" data-testid="create-invite-btn">
                <KeyRound className="w-4 h-4 mr-1.5" /> Generate Invite Link
              </Button>
            </div>
            {invites.length > 0 && (
              <div className="space-y-1.5" data-testid="invite-list">
                {invites.slice(0, 10).map((i) => (
                  <div key={i.code} className="flex items-center justify-between gap-2 text-sm bg-[#0B0F17] rounded px-3 py-2 border border-[#28364F]" data-testid={`invite-${i.code}`}>
                    <span className="font-mono tracking-widest">{i.code}</span>
                    {i.used ? (
                      <span className="text-xs text-[#64748B]">Used by {i.used_by}</span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-emerald-400 mr-2">Active</span>
                        <Button size="sm" variant="ghost" onClick={() => copyInvite(i.code)} data-testid={`copy-invite-${i.code}`}><Copy className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-400" onClick={() => revokeInvite(i.code)} data-testid={`revoke-invite-${i.code}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
          <Card className="bg-[#131B2A] border-[#28364F] p-4">
            <form onSubmit={createUser} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
              <div>
                <Label>Full name</Label>
                <Input required value={newUser.full_name} onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="new-user-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input required type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="new-user-email" />
              </div>
              <div>
                <Label>Phone (SMS)</Label>
                <Input value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  placeholder="+15551234567"
                  className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="new-user-phone" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#131B2A] border-[#28364F] text-white">
                    <SelectItem value="enforcer">Enforcer</SelectItem>
                    <SelectItem value="runner">Plate Runner</SelectItem>
                    <SelectItem value="removal">Removal Team</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Temp password</Label>
                <Input required minLength={6} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="new-user-password" />
              </div>
              <Button type="submit" className="bg-cyan-500 hover:bg-cyan-400 text-[#0B0F17] font-bold" data-testid="new-user-submit">
                <UserPlus className="w-4 h-4 mr-1.5" /> Add
              </Button>
            </form>
          </Card>

          <Card className="bg-[#131B2A] border-[#28364F] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-[#28364F] hover:bg-transparent">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="border-[#28364F] hover:bg-[#1C263B]" data-testid={`user-row-${u.email}`}>
                    <TableCell className="font-medium">{u.full_name}</TableCell>
                    <TableCell className="text-[#94A3B8]">{u.email}</TableCell>
                    <TableCell><Badge variant="outline" className="uppercase text-[10px] border-[#28364F]">{u.role}</Badge></TableCell>
                    <TableCell>
                      {u.active ? <span className="text-emerald-400 text-sm">Active</span> : <span className="text-red-400 text-sm">Disabled</span>}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button size="sm" variant="ghost" onClick={() => resetPassword(u)} title="Reset password" className="text-amber-300" data-testid={`reset-pw-${u.email}`}>
                        <KeyRound className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleActive(u)} data-testid={`toggle-${u.email}`}>
                        {u.active ? <ShieldOff className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteUser(u)} className="text-red-400" data-testid={`delete-${u.email}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="report" className="mt-4">
          <Card className="bg-[#131B2A] border-[#28364F] p-5 space-y-4">
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <Label>Shift date</Label>
                <Input type="date" value={reportDate} onChange={(e) => { setReportDate(e.target.value); setReportBlobUrl(null); }}
                  className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="report-date-input" />
              </div>
              <Dialog>
                <DialogTrigger asChild>
                  <Button onClick={previewReport} className="bg-cyan-500 hover:bg-cyan-400 text-[#0B0F17] font-bold" data-testid="generate-daily-report-button">
                    <FileText className="w-4 h-4 mr-1.5" /> Preview Daily PDF
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#131B2A] border-[#28364F] text-white max-w-4xl h-[85vh] flex flex-col">
                  <DialogHeader>
                    <DialogTitle className="flex items-center justify-between">
                      <span>Daily Shift Report — {reportDate}</span>
                      <Button onClick={downloadReport} size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-bold" data-testid="download-report-btn">
                        <Download className="w-4 h-4 mr-1.5" /> Download
                      </Button>
                    </DialogTitle>
                  </DialogHeader>
                  {reportBlobUrl ? (
                    <iframe src={reportBlobUrl} title="Report" className="flex-1 w-full bg-white rounded border border-[#28364F]" data-testid="report-iframe" />
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-[#94A3B8]">Loading report...</div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
            <p className="text-xs text-[#64748B]">
              Combined report includes every enforcer on shift, all vehicles tagged/booted/removed, times, fees, and removal agents.
            </p>
          </Card>

          <Card className="bg-[#131B2A] border-amber-500/30 p-5 space-y-4 mt-4">
            <div>
              <div className="font-display font-bold flex items-center gap-2"><CalendarRange className="w-4 h-4 text-amber-400" /> Weekly Summary (Payroll)</div>
              <p className="text-xs text-[#94A3B8]">Boots, removals and fees rolled up per officer, per removal agent, and per day.</p>
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <Label>Week starting (Monday)</Label>
                <Input type="date" value={weekStart} onChange={(e) => { setWeekStart(e.target.value); setWeekBlobUrl(null); }}
                  className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="week-start-input" />
              </div>
              <Dialog>
                <DialogTrigger asChild>
                  <Button onClick={previewWeekly} className="bg-amber-500 hover:bg-amber-400 text-[#0B0F17] font-bold" data-testid="generate-weekly-report-button">
                    <FileText className="w-4 h-4 mr-1.5" /> Preview Weekly PDF
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#131B2A] border-[#28364F] text-white max-w-4xl h-[85vh] flex flex-col">
                  <DialogHeader>
                    <DialogTitle className="flex items-center justify-between">
                      <span>Weekly Summary — week of {weekStart}</span>
                      <Button onClick={downloadWeekly} size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-bold" data-testid="download-weekly-btn">
                        <Download className="w-4 h-4 mr-1.5" /> Download
                      </Button>
                    </DialogTitle>
                  </DialogHeader>
                  {weekBlobUrl ? (
                    <iframe src={weekBlobUrl} title="Weekly Report" className="flex-1 w-full bg-white rounded border border-[#28364F]" data-testid="weekly-report-iframe" />
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-[#94A3B8]">Loading report...</div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!mediaFor} onOpenChange={(o) => !o && setMediaFor(null)}>
        <DialogContent className="bg-[#131B2A] border-[#28364F] text-white max-w-2xl" data-testid="media-dialog">
          <DialogHeader>
            <DialogTitle>Evidence — <span className="plate-badge">{mediaFor?.plate_number}</span></DialogTitle>
          </DialogHeader>
          {mediaFor && <EvidenceGallery v={mediaFor} cols="grid-cols-3 sm:grid-cols-4" className="h-28" testId="admin-media" />}
        </DialogContent>
      </Dialog>

      {editVehicle && (
        <VehicleEditModal vehicle={editVehicle} onClose={() => setEditVehicle(null)} onSaved={() => load()} />
      )}
      <Dialog open={!!chatCar} onOpenChange={(o) => !o && setChatCar(null)}>
        <DialogContent className="bg-[#131B2A] border-[#28364F] text-white max-w-md">
          <DialogHeader><DialogTitle>Chat — <span className="plate-badge">{chatCar?.plate_number}</span></DialogTitle></DialogHeader>
          {chatCar && <Chat scope="car" vehicleId={chatCar.id} height="h-72" />}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
